import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, FormGroup, Label, Input, Button } from "reactstrap";
import DollarInput from "components/DollarInput";
import ReactDatetime from "components/CustomDatePicker";
import moment from "moment";
import Select from "components/Select";
import { toNumber } from "utils/currency";
import { getCurrentDateForAPI, formatDateForPicker } from "../../../utils/dateUtils";
import { useCreateCustomerDeposit } from "../../../api/customer-deposits/service";
import { useGetSalesLocations } from "../../../api/sales-locations/service";
import {
  getTheme2SearchFilterBorderColor,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";

const CustomerDeposit = ({
  activeColor,
  activeColorStyle,
  isLightTheme,
  customerId,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();
  const createDepositMutation = useCreateCustomerDeposit();

  const { data: salesLocationsData, isLoading: isLoadingSalesLocations } =
    useGetSalesLocations(1, 1000, "");

  const theme2SelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );

  const DrawerSelect = useCallback(
    ({ styles, className = "react-select", ...selectProps }) => (
      <Select
        {...selectProps}
        className={className}
        styles={isTheme2 ? (styles ?? theme2SelectStyles) : styles}
      />
    ),
    [isTheme2, theme2SelectStyles]
  );

  const getFieldBorderColor = useCallback(
    () =>
      isTheme2
        ? getTheme2SearchFilterBorderColor()
        : isLightTheme
          ? "rgba(0,0,0,0.1)"
          : "rgba(255,255,255,0.1)",
    [isTheme2, isLightTheme]
  );

  const dateInputProps = useMemo(
    () => ({
      className: "form-control",
      placeholder: t("customerDetail.postDeposit.selectDate"),
      style: isTheme2
        ? { borderRadius: 0 }
        : {
            borderRadius: 0,
            backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
            color: isLightTheme ? "#1e1e2f" : "#fff",
            border: `1px solid ${getFieldBorderColor()}`,
          },
    }),
    [isTheme2, isLightTheme, getFieldBorderColor, t]
  );

  const [formData, setFormData] = useState({
    salesLocation: "",
    deliveryMethod: "Mail",
    postDate: getCurrentDateForAPI(),
    amountPaying: "$0.00",
    changeDue: "$0.00",
    paymentMethod: "ACH",
    effectiveDate: getCurrentDateForAPI(),
    amountReceived: "$0.00",
    refNumber: "",
  });

  useEffect(() => {
    if (createDepositMutation.isSuccess) {
      setFormData({
        salesLocation: "",
        deliveryMethod: "Mail",
        postDate: getCurrentDateForAPI(),
        amountPaying: "$0.00",
        changeDue: "$0.00",
        paymentMethod: "ACH",
        effectiveDate: getCurrentDateForAPI(),
        amountReceived: "$0.00",
        refNumber: "",
      });
    }
  }, [createDepositMutation.isSuccess]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePost = () => {
    if (!customerId) {
      console.error("Customer ID is required");
      return;
    }

    const convertDateToISO = (dateString) => {
      if (!dateString) return "";
      const date = moment(dateString, "YYYY-MM-DD");
      if (date.isValid()) {
        return date.toISOString();
      }
      return "";
    };

    const payload = {
      customer_id: customerId,
      sales_location: formData.salesLocation
        ? Number(formData.salesLocation)
        : null,
      delivery_method: formData.deliveryMethod || "",
      payment_method: formData.paymentMethod || "",
      post_date: convertDateToISO(formData.postDate),
      effective_date: convertDateToISO(formData.effectiveDate),
      amount_paying: toNumber(formData.amountPaying) || 0,
      amount_received: toNumber(formData.amountReceived) || 0,
      change_due: toNumber(formData.changeDue) || 0,
      ref_number: formData.refNumber || "",
    };

    createDepositMutation.mutate(payload);
  };

  const deliveryMethodOptions = [
    { value: "Walk-In", label: "Walk-In" },
    { value: "Mail", label: "Mail" },
    { value: "Phone", label: "Phone" },
    { value: "Web", label: "Web" },
    { value: "Fax", label: "Fax" },
    { value: "Lockbox", label: "Lockbox" },
    { value: "Email", label: "Email" },
    { value: "TEXT", label: "TEXT" },
    { value: "IVR", label: "IVR" },
    { value: "Kiosk", label: "Kiosk" },
  ];

  const paymentMethodOptions = [
    { value: "ACH", label: "ACH" },
    { value: "Check", label: "Check" },
    { value: "Cash", label: "Cash" },
    { value: "Credit Card", label: "Credit Card" },
  ];

  const salesLocationOptions = useMemo(() => {
    const results =
      salesLocationsData?.data || salesLocationsData?.results || [];
    if (!results || !Array.isArray(results)) return [];

    return results.map((location) => ({
      value: location.id,
      label:
        location.channel?.name ||
        location.name ||
        location.location_code ||
        location.dealer_number ||
        `Sales Location ${location.id}`,
    }));
  }, [salesLocationsData]);

  const dollarInputClassName = isTheme2 ? "form-control" : "";
  const dollarInputStyle = isTheme2
    ? { borderRadius: 0, fontSize: "14px" }
    : undefined;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handlePost}
          disabled={createDepositMutation.isPending || !customerId}
          style={{
            ...activeColorStyle,
            color: "white",
            padding: "8px 16px",
            fontWeight: 600,
            fontSize: 14,
            opacity:
              createDepositMutation.isPending || !customerId ? 0.5 : 1,
            cursor:
              createDepositMutation.isPending || !customerId
                ? "not-allowed"
                : "pointer",
          }}
        >
          {createDepositMutation.isPending
            ? t("customerDetail.postDeposit.processing") || "Processing..."
            : t("customerDetail.postDeposit.post")}
        </Button>
      </div>

      <h6
        style={{
          marginBottom: 24,
          color: isLightTheme ? "#1e1e2f" : "#ffffff",
          fontWeight: 600,
        }}
      >
        {t("customerDetail.postDeposit.title")}
      </h6>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.salesLocation")}
            </Label>
            <DrawerSelect
              activeColor={activeColor}
              className="react-select"
              placeholder={t("customerDetail.postDeposit.selectSalesLocation")}
              isSearchable
              closeMenuOnSelect={true}
              value={
                formData.salesLocation
                  ? {
                      value: formData.salesLocation,
                      label:
                        salesLocationOptions.find(
                          (opt) => opt.value === formData.salesLocation
                        )?.label || formData.salesLocation,
                    }
                  : null
              }
              onChange={(option) =>
                handleInputChange("salesLocation", option?.value || "")
              }
              options={salesLocationOptions}
              isLoading={isLoadingSalesLocations}
              isClearable
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.paymentMethod")}
            </Label>
            <DrawerSelect
              activeColor={activeColor}
              className="react-select"
              placeholder={t("customerDetail.postDeposit.selectPaymentMethod")}
              isSearchable={false}
              closeMenuOnSelect={true}
              value={
                formData.paymentMethod
                  ? {
                      value: formData.paymentMethod,
                      label: formData.paymentMethod,
                    }
                  : null
              }
              onChange={(option) =>
                handleInputChange("paymentMethod", option?.value || "")
              }
              options={paymentMethodOptions}
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.deliveryMethod")}
            </Label>
            <DrawerSelect
              activeColor={activeColor}
              className="react-select"
              placeholder={t("customerDetail.postDeposit.selectDeliveryMethod")}
              isSearchable={false}
              closeMenuOnSelect={true}
              value={
                formData.deliveryMethod
                  ? {
                      value: formData.deliveryMethod,
                      label: formData.deliveryMethod,
                    }
                  : null
              }
              onChange={(option) =>
                handleInputChange("deliveryMethod", option?.value || "")
              }
              options={deliveryMethodOptions}
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.effectiveDate")}
            </Label>
            <ReactDatetime
              inputProps={dateInputProps}
              dateFormat="MM-DD-YYYY"
              timeFormat={false}
              closeOnSelect={true}
              value={
                formData.effectiveDate
                  ? formatDateForPicker(formData.effectiveDate)
                  : null
              }
              onChange={(date) => {
                const value =
                  date && moment(date).isValid()
                    ? moment(date).format("YYYY-MM-DD")
                    : "";
                handleInputChange("effectiveDate", value);
              }}
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.postDate")}
            </Label>
            <ReactDatetime
              inputProps={dateInputProps}
              dateFormat="MM-DD-YYYY"
              timeFormat={false}
              closeOnSelect={true}
              value={
                formData.postDate
                  ? formatDateForPicker(formData.postDate)
                  : null
              }
              onChange={(date) => {
                const value =
                  date && moment(date).isValid()
                    ? moment(date).format("YYYY-MM-DD")
                    : "";
                handleInputChange("postDate", value);
              }}
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.amountReceived")}
            </Label>
            <DollarInput
              name="amountReceived"
              className={dollarInputClassName}
              style={dollarInputStyle}
              value={formData.amountReceived}
              onChange={(value) => handleInputChange("amountReceived", value)}
              placeholder="$0.00"
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.amountPaying")}
            </Label>
            <DollarInput
              name="amountPaying"
              className={dollarInputClassName}
              style={dollarInputStyle}
              value={formData.amountPaying}
              onChange={(value) => handleInputChange("amountPaying", value)}
              placeholder="$0.00"
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.changeDue")}
            </Label>
            <DollarInput
              name="changeDue"
              className={dollarInputClassName}
              style={dollarInputStyle}
              value={formData.changeDue}
              onChange={(value) => handleInputChange("changeDue", value)}
              placeholder="$0.00"
            />
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label
              style={{
                fontSize: 12,
                color: isLightTheme ? "#666" : "#a0a0b2",
                marginBottom: 8,
              }}
            >
              {t("customerDetail.postDeposit.refNumber")}
            </Label>
            <Input
              type="text"
              name="refNumber"
              className={isTheme2 ? "form-control" : undefined}
              value={formData.refNumber}
              onChange={(e) => handleInputChange("refNumber", e.target.value)}
              placeholder={t("customerDetail.postDeposit.enterRefNumber")}
              style={
                isTheme2
                  ? {
                      backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                      color: isLightTheme ? "#1e1e2f" : "#fff",
                    }
                  : {
                      backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                      color: isLightTheme ? "#1e1e2f" : "#fff",
                      border: `1px solid ${getFieldBorderColor()}`,
                    }
              }
            />
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

export default CustomerDeposit;
